<template>
<!-- Packing list detail (saved id or draft) -->
  <div
    class="w-full flex flex-col flex-grow flex-auto h-0 min-h-0 p-1"
    :class="previewOnlyLayout ? 'overflow-hidden' : 'overflow-y-auto'"
  >
<!-- Document open / Mongo load errors -->
    <WorkingAndError
      class="w-full mb-1"
      :is-working="isOpeningDocument"
      :error="documentError"
      @clear-error="documentError = null"
    />
    <WorkingAndError
      v-if="packingListId && (isLoadingPackingList || packingListLoadError)"
      class="w-full mb-1"
      :is-working="isLoadingPackingList && !packingListLoadError"
      :error="packingListLoadError"
      @clear-error="packingListLoadError = null"
    />
<!-- Main content when list is loaded -->
    <div
      v-if="packingList"
      class="flex flex-col w-full min-h-0 flex-1"
      :class="previewOnlyLayout ? 'overflow-hidden' : ''"
    >
      <div class="w-full flex-shrink-0">
<!-- Receiving issues (edit or read-only) + notes -->
      <div v-if="showReceivingIssuesRow && canEditReceivingIssues" class="w-full flex items-center flex-wrap">
        <div class="p-1 mr-2">
          <button
            type="button"
            class="bn-text"
            :class="{ 'font-semibold text-amber': receivingIssuesState.missingItems, 'opacity-40': !receivingIssuesState.missingItems }"
            :disabled="receivingIssuesUpdating"
            @click="toggleReceivingIssueMissing"
          >
            <XIcon v-if="!receivingIssuesState.missingItems" />
            <CheckIcon v-else />
            <div>Missing items</div>
          </button>
        </div>
        <div class="p-1 mr-2">
          <button
            type="button"
            class="bn-text"
            :class="{ 'font-semibold text-amber': receivingIssuesState.inaccurate, 'opacity-40': !receivingIssuesState.inaccurate }"
            :disabled="receivingIssuesUpdating"
            @click="toggleReceivingIssueInaccurate"
          >
            <XIcon v-if="!receivingIssuesState.inaccurate" />
            <CheckIcon v-else />
            <div>Inaccurate</div>
          </button>
        </div>
        <div v-if="receivingIssuesState.damagedItems" class="p-1 mr-2">
          <button
            type="button"
            class="bn-text"
            :class="{ 'font-semibold text-red': receivingIssuesState.damagedItems, 'opacity-60': !receivingIssuesState.damagedItems }"
            disabled
            title="Set automatically when damaged equipment is recorded during receiving."
          >
            Damaged items
          </button>
        </div>
      </div>
      <div
        v-else-if="showReceivingIssuesRow && !canEditReceivingIssues"
        class="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pb-1 text-left text-xs opacity-90"
      >
        <span v-if="receivingIssuesState.missingItems" class="text-amber font-semibold">Missing items</span>
        <span v-if="receivingIssuesState.inaccurate" class="text-amber font-semibold">Inaccurate</span>
        <span v-if="receivingIssuesState.damagedItems" class="text-red font-semibold">Damaged items</span>
        <span v-if="!receivingIssuesState.missingItems && !receivingIssuesState.inaccurate && !receivingIssuesState.damagedItems" class="opacity-60">
          No receiving issues flagged
        </span>
      </div>
      <div v-if="showReceivingIssuesRow && canEditReceivingIssues && showReceivingIssueNotes" class="w-full flex flex-col px-1 pb-1 mb-1">
        <label class="text-left text-sky font-semibold mb-1">Issue notes</label>
        <textarea
          v-model.trim="receivingIssueNotesDraft"
          v-auto-resize
          rows="2"
          maxlength="2000"
          class="w-full resize-none border border-indigo-sky text-left max-h-24 overflow-y-auto"
          placeholder="Describe missing items or list inaccuracies…"
          :disabled="receivingIssuesUpdating"
          @blur="saveReceivingIssueNotes"
        />
      </div>
      <div
        v-else-if="showReceivingIssuesRow && !canEditReceivingIssues && (packingList?.receivingIssueNotes || '').trim().length > 0"
        class="w-full flex flex-col px-1 pb-1 mb-1 text-left text-xs"
      >
        <span class="text-sky font-semibold mb-0.5">Issue notes</span>
        <div class="whitespace-pre-wrap">{{ packingList.receivingIssueNotes }}</div>
      </div>
      <WorkingAndError
        v-if="showReceivingIssuesRow && canEditReceivingIssues"
        class="w-full px-1"
        :is-working="receivingIssuesUpdating"
        :error="receivingIssuesError"
        @clear-error="receivingIssuesError = null"
      />
      </div>
<!-- Extracted header details + additional fields -->
      <div class="w-full px-2 p-1 flex flex-col md:flex-row md:items-stretch md:space-x-3 min-h-0" :class="previewOnlyLayout ? 'flex-1 overflow-hidden' : 'flex-shrink-0'">
        <div v-if="hasAnyExtractedData" class="w-full md:w-2/3 space-y-2 max-h-24 sm:max-h-96 sm:min-h-72 overflow-y-auto">
          <div v-if="hasExtractedDetails" class="w-full">
            <div class="w-full panel-opaque p-1">
              <div class="w-full flex items-center flex-wrap px-1">
                <div class="w-full flex flex-wrap items-center justify-between">
                  <div v-if="displayTrackingNumber" class="w-auto mr-4 flex items-center flex-wrap-reverse sm:flex-nowrap">
                    <div class="text-left text-sky p-0.5">Tracking Number:</div>
                    <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ displayTrackingNumber }}</div>
                  </div>
                  <div v-if="packingList.details?.shipDate" class="w-auto mr-4 flex items-center flex-nowrap">
                    <div class="text-left text-sky p-0.5">Ship Date:</div>
                    <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.shipDate }}</div>
                  </div>
                  <div v-if="packingList.details?.orderNumber" class="w-auto mr-4 flex items-center flex-nowrap">
                    <div class="text-left text-sky p-0.5">Order Number:</div>
                    <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.orderNumber }}</div>
                  </div>
                  <div class="w-auto mr-4 flex items-center flex-nowrap">
                    <div class="text-left text-sky p-0.5">Purchase Order:</div>
                    <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ (packingList.details?.purchaseOrder || '').toUpperCase() || '—' }}</div>
                  </div>
                  <div v-if="packingList.details?.customerNumber" class="w-auto mr-4 flex items-center flex-nowrap">
                    <div class="text-left text-sky p-0.5">Customer Number:</div>
                    <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.customerNumber }}</div>
                  </div>
                  <div v-if="packingList.details?.shipmentNumber" class="w-auto mr-4 flex items-center flex-nowrap">
                    <div class="text-left text-sky p-0.5">Shipment Number:</div>
                    <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.shipmentNumber }}</div>
                  </div>
                </div>
                <div v-if="packingList.details?.carrier" class="w-full flex items-center flex-wrap">
                  <div class="text-left text-sky w-16 p-0.5">Carrier:</div>
                  <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.carrier }}</div>
                </div>
                <div v-if="packingList.details?.fromAddress" class="w-full flex items-center flex-wrap">
                  <div class="text-left text-sky p-0.5 w-16">From:</div>
                  <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.fromAddress }}</div>
                </div>
                <div v-if="packingList.details?.toAddress" class="w-full flex items-center flex-wrap">
                  <div class="text-left text-sky w-16 p-0.5">To:</div>
                  <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.toAddress }}</div>
                </div>
                <div v-if="packingList.details?.billToAddress" class="w-full flex items-center flex-wrap">
                  <div class="text-left text-sky w-16 p-0.5">Bill To:</div>
                  <div class="flex-grow whitespace-break-spaces truncate text-left p-0.5">{{ packingList.details.billToAddress }}</div>
                </div>
                <div v-if="packingList.additionalTrackingNumbers?.length" class="w-full flex items-center flex-wrap">
                  <div class="text-left text-sky p-0.5 mr-2">More Tracking:</div>
                  <div class="flex-grow flex flex-wrap items-center gap-1 p-0.5">
                    <span v-for="tn in packingList.additionalTrackingNumbers" :key="'atn-' + tn" class="chip-sky">{{ tn }}</span>
                  </div>
                </div>
                <div v-if="packingList.additionalPurchaseOrders?.length" class="w-full flex items-center flex-wrap">
                  <div class="text-left text-sky p-0.5 mr-2">More POs:</div>
                  <div class="flex-grow flex flex-wrap items-center gap-1 p-0.5">
                    <span v-for="po in packingList.additionalPurchaseOrders" :key="'apo-' + po" class="chip-indigo">{{ (po || '').toUpperCase() }}</span>
                  </div>
                </div>
                <div v-if="packingList.details?.notes" class="w-full text-left whitespace-normal truncate max-h-24 overflow-y-auto">
                  <span class="text-left text-sky min-w-16 p-0.5 mr-2">Notes:</span>{{ packingList.details.notes }}
                </div>
              </div>
            </div>
          </div>
          <div v-if="hasExtractedAdditionalDetails" class="w-full">
            <div class="panel-opaque p-2 space-y-0.5 flex items-center flex-wrap">
              <div
                v-for="(val, key) in packingList.additionalDetails"
                :key="'add-' + key"
                v-show="val != null && val !== ''"
                class="flex p-1 mr-2 justify-start items-center"
              >
                <span class="text-indigo text-left mr-1">{{ formatDetailKey(key) }}:</span>
                <span class="text-left">{{ formatDetailValue(val) }}</span>
              </div>
            </div>
          </div>
        </div>

<!-- PDF / image preview (Drive file) -->
        <div v-if="packingList?.googleFileId" class="w-full mt-2 md:mt-0 flex flex-col min-h-0" :class="previewOnlyLayout ? 'min-h-[250px] flex-1 md:w-full md:mt-0' : 'md:w-1/3'">
          <div
            class="flex flex-grow w-full min-h-[200px] max-h-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-200 dark:bg-neutral-900 overflow-hidden flex-col"
            :class="previewOnlyLayout ? 'flex-1 min-h-0' : ''"
          >
            <div v-if="previewObjectUrl" class="w-full flex flex-col flex-1 min-h-0 overflow-hidden">
              <iframe
                v-if="isPdfPreview"
                :src="pdfIframeSrc"
                class="w-full flex-1 min-h-0 h-full max-h-full rounded border-0"
                title="Packing list document"
              />
              <div
                v-else-if="isImagePreview"
                class="flex-1 min-h-0 max-h-full overflow-auto flex items-center justify-center"
              >
                <img
                  :src="previewObjectUrl"
                  alt="Packing list document"
                  class="max-h-full max-w-full w-auto h-auto object-contain"
                />
              </div>
              <div class="flex-shrink-0 p-1 flex items-center justify-end border-t border-neutral-300 dark:border-neutral-700">
                <button
                  class="bn-text"
                  :disabled="isOpeningDocument || !canAccessPackingListFiles"
                  @click="openPackingListDocument"
                >
                  View full document
                </button>
              </div>
            </div>
            <div
              v-else
              class="w-full flex flex-col items-center justify-center p-4 opacity-80 min-h-[12rem]"
              :class="previewOnlyLayout ? 'flex-1' : ''"
            >
              <RefreshIcon v-if="isLoadingPreview" class="w-8 h-8 animate-spin-slow mb-2" />
              <span v-if="isLoadingPreview">Loading document…</span>
              <span v-else-if="!canAccessPackingListFiles">You do not have permission to load packing list documents.</span>
              <span v-else>Document preview</span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="hasParsedLineItems" class="w-full flex-shrink-0 whitespace-normal truncate text-left p-1">
        * AI-extracted line items depend on the quality and format of the packing list and may contain inaccuracies.
      </div>
<!-- Parsed line items + optional draft edits -->
      <div v-if="hasParsedLineItems || allowLineItemEdits" class="w-full flex flex-col flex-grow border-t">
        <div class="w-full flex flex-col flex-grow flex-auto h-0 min-h-0">
          <div class="w-full flex items-center flex-wrap p-1">
            <div v-if="hasParsedLineItems" class="text-left font-semibold text-sky flex-grow">Line items ({{ lineItems.length }})</div>
            <div v-if="allowLineItemEdits" class="w-auto">
              <button class="bn-text text-sky" type="button" @click="addLineItem">Add line item</button>
            </div>
          </div>
          <div class="w-full flex flex-col flex-grow flex-auto h-0 min-h-28 p-1 space-y-0.5 text-left overflow-y-auto">
            <div v-for="(item, i) in lineItems" :key="'parsed-' + i" class="w-full flex items-center gap-x-2 border-b pb-1">
              <div class="w-20 flex items-center justify-start shrink-0">
                <span class="text-sky w-8 p-0.5">Qty</span>
                <span class="w-14 text-left p-0.5">{{ item.qty }}</span>
              </div>
              <span class="flex-grow min-w-0 text-left p-0.5">{{ item.partNumber ? capitalizeString(item.partNumber) + ' - ' + (item.description || '') : (item.description || '') }}</span>
            </div>
            <div v-if="lineItemEditsEnabled" class="w-full">
              <div v-if="Array.isArray(addedLineItems) && addedLineItems.length" class="w-full flex flex-col flex-grow flex-auto h-0 min-h-12 p-1 space-y-1 text-left">
                <div v-for="(item, i) in addedLineItems" :key="'added-' + i" class="w-full flex flex-wrap items-center gap-x-2 border-b pb-1">
                  <div class="w-20 flex items-center shrink-0">
                    <input
                      :value="item.qty"
                      type="number"
                      class="w-14 text-center"
                      min="0"
                      max="99999"
                      placeholder="Qty"
                      @input="patchAdded(i, 'qty', $event.target.value)"
                    />
                  </div>
                  <div class="w-full sm:w-auto sm:flex-grow min-w-0 flex items-center">
                    <input
                      :value="item.description"
                      type="text"
                      class="flex-grow min-w-0 text-left"
                      placeholder="Description"
                      @input="patchAdded(i, 'description', $event.target.value)"
                    />
                  </div>
                  <div class="w-full sm:w-auto min-w-0 flex flex-nowrap items-center">
                    <div class="flex-grow sm:w-40 flex items-center shrink-0 truncate">
                      <input
                        :value="item.partNumber"
                        type="text"
                        class="w-full text-left"
                        placeholder="Part #"
                        @input="patchAdded(i, 'partNumber', $event.target.value)"
                      />
                    </div>
                    <div class="p-1">
                      <button class="bn-icon-only shrink-0" type="button" :aria-label="'Remove line'" @click="removeAddedItem(i)">
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import api from '@/api'
import WorkingAndError from '@/components/ui/WorkingAndError.vue'
import autoResize from '@/components/ui/autoResize'
import { viewOrDownloadFile, capitalizeString } from '@/shared'
import { TrashIcon, RefreshIcon, XIcon, CheckIcon } from '@heroicons/vue/solid'

export default {
  name: 'PackingListDetailPanel',
  directives: { autoResize },
  components: { WorkingAndError, TrashIcon, RefreshIcon, XIcon, CheckIcon },
  props: {
    // Document source — saved Mongo id or in-memory draft (parsed upload)
    /**
     * Saved packing list id — when set, document is loaded with GET /equipment/packingLists/:id and owned internally.
     */
    packingListId: {
      type: String,
      default: null
    },
    /**
     * In-memory packing list before save (parsed upload, no _id). Ignored when packingListId is set.
     */
    draftPackingList: {
      type: Object,
      default: null
    },
    // Draft POST line items (parent-owned v-model)
    addedLineItems: {
      type: Array,
      default: () => []
    },
    allowLineItemEdits: {
      type: Boolean,
      default: false
    },
    // Preview & custom API routes (external secure links, etc.)
    loadDocumentPreview: {
      type: Boolean,
      default: true
    },
    getFileUrl: {
      type: Function,
      default: null
    },
    /** When set, called with packing list id to build GET URL (e.g. external secure-code routes). */
    packingListGetUrlResolver: {
      type: Function,
      default: null
    },
    /** When set, called with packing list id for PUT receivingIssues. Defaults to logistics route. */
    packingListReceivingIssuesPutUrlResolver: {
      type: Function,
      default: null
    },
  },
  emits: ['update:addedLineItems', 'packingListUpdated'],
  setup(props, { emit }) {
    // Inject & access
      const global = inject('global')
      const { sendChangeEvent, authenticated } = global || {}

      const authUser = computed(() => authenticated?.value)
      const accessModel = computed(() => authUser.value?.role?.accessModel ?? {})

      /** Matches PUT .../receivingIssues — accessControl({ receiving: 1 }). */
      const canEditReceivingIssues = computed(
        () => !!authUser.value?.loggedIn && Number(accessModel.value.receiving ?? 0) >= 1
      )

      /**
       * Matches GET packing list by id and GET file blob — accessControlAny({ projects: 1, receiving: 1 }).
       * Not logged in (e.g. external secure link): file routes use secureProjectCode, not role.
       */
      const canAccessPackingListFiles = computed(() => {
        if (!authUser.value?.loggedIn) return true
        const p = Number(accessModel.value.projects ?? 0)
        const r = Number(accessModel.value.receiving ?? 0)
        return p >= 1 || r >= 1
      })

      /** Draft line-item UI (POST packing list requires receiving access). */
      const lineItemEditsEnabled = computed(() => props.allowLineItemEdits && canEditReceivingIssues.value)

    // Refs — packing list & receiving error state
      const localPackingList = ref(null)
      const isLoadingPackingList = ref(false)
      const packingListLoadError = ref(null)
      const receivingIssuesUpdating = ref(false)
      const receivingIssuesError = ref(null)

    // Computed — exposed packing list ref
      const packingList = computed(() => localPackingList.value)

    // Helpers — GET/PUT URLs & socket notify
      const notifyReceivingIssuesOrNotesChanged = (packingListId) => {
        if (!sendChangeEvent || !packingListId) return
        sendChangeEvent('packingList', { packingListId: String(packingListId) })
      }

      const packingListGetUrl = (id) =>
        typeof props.packingListGetUrlResolver === 'function'
          ? props.packingListGetUrlResolver(id)
          : `logistics/equipment/packingLists/${id}`

      const packingListReceivingIssuesPutUrl = (id) =>
        typeof props.packingListReceivingIssuesPutUrlResolver === 'function'
          ? props.packingListReceivingIssuesPutUrlResolver(id)
          : `logistics/equipment/packingLists/${id}/receivingIssues`

    // API — load by id; id/draft watchers; mount listeners
      const loadPackingListById = async (opts = {}) => {
        const silent = opts.silent === true
        const id = props.packingListId
        if (!id) return
        if (authUser.value?.loggedIn && !canAccessPackingListFiles.value) {
          if (!silent) {
            packingListLoadError.value = 'Your role does not have access to packing lists.'
            localPackingList.value = null
            isLoadingPackingList.value = false
          }
          return
        }
        if (!silent) {
          isLoadingPackingList.value = true
          packingListLoadError.value = null
        }
        try {
          const res = await api.get(packingListGetUrl(id))
          const doc = res.data?.data
          if (doc) {
            localPackingList.value = doc
            emit('packingListUpdated', doc)
          }
        } catch (err) {
          if (!silent) {
            packingListLoadError.value = err.response?.data?.error || err.message || 'Failed to load packing list.'
            localPackingList.value = null
          }
        } finally {
          if (!silent) isLoadingPackingList.value = false
        }
      }

      const onPackingListReceivingIssuesBroadcast = (e) => {
        if (e.detail?.type !== 'packingList') return
        if (receivingIssuesUpdating.value) return
        const broadcastPackingListId = e.detail?.data?.packingListId
        const activePackingListId = props.packingListId
        if (
          !broadcastPackingListId ||
          !activePackingListId ||
          String(broadcastPackingListId) !== String(activePackingListId)
        ) {
          return
        }
        loadPackingListById({ silent: true })
      }

      onMounted(() => {
        window.addEventListener('local_data_change', onPackingListReceivingIssuesBroadcast)
        window.addEventListener('data_change_from_socket', onPackingListReceivingIssuesBroadcast)
      })

      watch(
        () => props.packingListId,
        (id) => {
          if (id) {
            loadPackingListById()
          } else {
            packingListLoadError.value = null
            isLoadingPackingList.value = false
            localPackingList.value = props.draftPackingList ? { ...props.draftPackingList } : null
          }
        },
        { immediate: true }
      )

      watch(
        () => props.draftPackingList,
        (d) => {
          if (!props.packingListId && d) {
            localPackingList.value = { ...d }
          }
        },
        { deep: true }
      )

    // Receiving issues — flags, PATCH, notes draft
      const showReceivingIssuesRow = computed(
        () => !!(localPackingList.value?._id ?? localPackingList.value?.id)
      )

      const receivingIssuesState = computed(() => {
        const ri = localPackingList.value?.receivingIssues
        return {
          damagedItems: !!ri?.damagedItems,
          missingItems: !!ri?.missingItems,
          inaccurate: !!ri?.inaccurate
        }
      })

      const showReceivingIssueNotes = computed(
        () => receivingIssuesState.value.missingItems || receivingIssuesState.value.inaccurate
      )

      const receivingIssueNotesDraft = ref('')

      watch(
        () => localPackingList.value?.receivingIssueNotes,
        (v) => {
          receivingIssueNotesDraft.value = v == null || v === '' ? '' : String(v)
        },
        { immediate: true }
      )

      const patchReceivingIssues = async (partial) => {
        if (!canEditReceivingIssues.value) return
        const id = localPackingList.value?._id ?? localPackingList.value?.id
        if (!id) return
        receivingIssuesError.value = null
        receivingIssuesUpdating.value = true
        try {
          const body = {
            missingItems: partial.missingItems !== undefined ? partial.missingItems : receivingIssuesState.value.missingItems,
            inaccurate: partial.inaccurate !== undefined ? partial.inaccurate : receivingIssuesState.value.inaccurate
          }
          const res = await api.put(packingListReceivingIssuesPutUrl(id), body)
          const doc = res.data?.data
          if (doc) {
            localPackingList.value = { ...localPackingList.value, ...doc }
            emit('packingListUpdated', doc)
            notifyReceivingIssuesOrNotesChanged(id)
          }
        } catch (err) {
          receivingIssuesError.value =
            err.response?.data?.error || err.message || 'Failed to update receiving issues.'
        } finally {
          receivingIssuesUpdating.value = false
        }
      }

      const toggleReceivingIssueMissing = () => {
        patchReceivingIssues({ missingItems: !receivingIssuesState.value.missingItems })
      }

      const toggleReceivingIssueInaccurate = () => {
        patchReceivingIssues({ inaccurate: !receivingIssuesState.value.inaccurate })
      }

      const saveReceivingIssueNotes = async () => {
        if (!canEditReceivingIssues.value) return
        if (!showReceivingIssueNotes.value) return
        const id = localPackingList.value?._id ?? localPackingList.value?.id
        if (!id) return
        const trimmed = (receivingIssueNotesDraft.value ?? '').trim()
        const stored = String(localPackingList.value?.receivingIssueNotes ?? '').trim()
        if (trimmed === stored) return
        receivingIssuesUpdating.value = true
        receivingIssuesError.value = null
        try {
          const res = await api.put(packingListReceivingIssuesPutUrl(id), {
            receivingIssueNotes: trimmed.length ? trimmed : null
          })
          const doc = res.data?.data
          if (doc) {
            localPackingList.value = { ...localPackingList.value, ...doc }
            emit('packingListUpdated', doc)
            notifyReceivingIssuesOrNotesChanged(id)
          }
        } catch (err) {
          receivingIssuesError.value =
            err.response?.data?.error || err.message || 'Failed to save issue notes.'
        } finally {
          receivingIssuesUpdating.value = false
        }
      }

    // Document preview — blob load & open full file
      const documentError = ref(null)
      const isOpeningDocument = ref(false)
      const previewObjectUrl = ref(null)
      const previewMimeType = ref(null)
      const isLoadingPreview = ref(false)

      const revokePreviewUrl = () => {
        if (previewObjectUrl.value) {
          URL.revokeObjectURL(previewObjectUrl.value)
          previewObjectUrl.value = null
          previewMimeType.value = null
        }
      }

      const fileBlobUrl = (fileId) =>
        props.getFileUrl ? props.getFileUrl(fileId) : `logistics/equipment/packingLists/file/${fileId}`

      const loadPackingListPreview = async () => {
        const fileId = localPackingList.value?.googleFileId
        if (!fileId || isLoadingPreview.value || !props.loadDocumentPreview || !canAccessPackingListFiles.value) return
        revokePreviewUrl()
        isLoadingPreview.value = true
        documentError.value = null
        try {
          const res = await api.get(fileBlobUrl(fileId), { responseType: 'blob' })
          const mimeType = res.headers?.['content-type']?.split(';')[0]?.trim() || 'application/pdf'
          const blob = new Blob([res.data], { type: mimeType })
          previewObjectUrl.value = URL.createObjectURL(blob)
          previewMimeType.value = mimeType
        } catch (err) {
          documentError.value = err.response?.data?.error || err.message || 'Failed to load preview.'
        } finally {
          isLoadingPreview.value = false
        }
      }

      const isPdfPreview = computed(() => (previewMimeType.value || '').toLowerCase() === 'application/pdf')
      const isImagePreview = computed(() => (previewMimeType.value || '').toLowerCase().startsWith('image/'))

      const pdfIframeSrc = computed(() => {
        const url = previewObjectUrl.value
        if (!url) return ''
        const base = url.split('#')[0]
        return `${base}#toolbar=0&navpanes=0`
      })

    // Computed — extracted details, line items, layout
      const isNonEmptyDetailValue = (v) => {
        if (v == null) return false
        if (typeof v === 'string') return v.trim().length > 0
        if (typeof v === 'number') return Number.isFinite(v)
        if (typeof v === 'object') return Object.keys(v).length > 0
        return String(v).trim().length > 0
      }

      const displayTrackingNumber = computed(() => {
        const pl = localPackingList.value
        if (!pl) return ''
        const root = pl.trackingNumber != null && String(pl.trackingNumber).trim() !== '' ? String(pl.trackingNumber).trim() : ''
        const det =
          pl.details?.trackingNumber != null && String(pl.details.trackingNumber).trim() !== ''
            ? String(pl.details.trackingNumber).trim()
            : ''
        return root || det || ''
      })

      const hasExtractedDetails = computed(() => {
        const pl = localPackingList.value
        if (pl?.trackingNumber != null && String(pl.trackingNumber).trim() !== '') return true
        const d = pl?.details
        if (!d || typeof d !== 'object') return false
        return Object.values(d).some(isNonEmptyDetailValue)
      })

      const hasExtractedAdditionalDetails = computed(() => {
        const ad = localPackingList.value?.additionalDetails
        if (!ad || typeof ad !== 'object') return false
        return Object.entries(ad).some(
          ([, v]) => v != null && String(v).trim() !== ''
        )
      })

      const lineItems = computed(() => localPackingList.value?.lineItems ?? [])

      const hasParsedLineItems = computed(() => {
        const items = lineItems.value
        if (!Array.isArray(items) || items.length === 0) return false
        return items.some((item) => {
          const desc = (item?.description ?? '').trim()
          const pn = (item?.partNumber ?? '').trim()
          const qty = item?.qty
          const hasQty = qty != null && qty !== '' && Number.isFinite(Number(qty))
          return hasQty || desc.length > 0 || pn.length > 0
        })
      })

      const hasAnyExtractedData = computed(
        () =>
          hasExtractedDetails.value ||
          hasExtractedAdditionalDetails.value ||
          hasParsedLineItems.value
      )

      const previewOnlyLayout = computed(
        () => !!(localPackingList.value?.googleFileId && !hasAnyExtractedData.value)
      )

    // Watch — preview when Drive file id changes
      const previewGoogleFileId = computed(() => localPackingList.value?.googleFileId ?? null)

      watch(
        [previewGoogleFileId, () => props.loadDocumentPreview, () => props.getFileUrl, canAccessPackingListFiles],
        ([fileId, load]) => {
          revokePreviewUrl()
          if (load && fileId && canAccessPackingListFiles.value) {
            loadPackingListPreview()
          }
        },
        { immediate: true }
      )

      onBeforeUnmount(() => {
        window.removeEventListener('local_data_change', onPackingListReceivingIssuesBroadcast)
        window.removeEventListener('data_change_from_socket', onPackingListReceivingIssuesBroadcast)
        revokePreviewUrl()
      })

    // Format helpers (details grid)
      const formatDetailKey = (key) => {
        return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim()
      }
      const formatDetailValue = (val) => {
        if (val == null) return ''
        if (typeof val === 'string' || typeof val === 'number') return String(val)
        if (typeof val === 'object') return JSON.stringify(val)
        return String(val)
      }

    // Handlers — open/download original file
      const openPackingListDocument = async () => {
        const fileId = localPackingList.value?.googleFileId
        if (!fileId || !canAccessPackingListFiles.value) return
        documentError.value = null
        isOpeningDocument.value = true
        try {
          const res = await api.get(fileBlobUrl(fileId), { responseType: 'blob' })
          const mimeType = res.headers?.['content-type']?.split(';')[0]?.trim() || 'application/pdf'
          const blob = new Blob([res.data], { type: mimeType })
          viewOrDownloadFile(blob, mimeType, 'Packing list')
        } catch (err) {
          documentError.value = err.response?.data?.error || err.message || 'Failed to open document.'
        } finally {
          isOpeningDocument.value = false
        }
      }

    // Handlers — draft line items (parent v-model)
      const addLineItem = () => {
        emit('update:addedLineItems', [...props.addedLineItems, { description: '', qty: null, partNumber: '' }])
      }

      const removeAddedItem = (index) => {
        emit(
          'update:addedLineItems',
          props.addedLineItems.filter((_, i) => i !== index)
        )
      }

      const patchAdded = (index, field, raw) => {
        const next = props.addedLineItems.map((row, j) => {
          if (j !== index) return row
          if (field === 'qty') {
            const v = raw === '' ? null : Number(raw)
            return { ...row, qty: Number.isFinite(v) ? v : row.qty }
          }
          return { ...row, [field]: raw }
        })
        emit('update:addedLineItems', next)
      }

    return {
      capitalizeString,
      // Packing list & access
      isLoadingPackingList,
      packingListLoadError,
      packingList,
      canEditReceivingIssues,
      canAccessPackingListFiles,
      lineItemEditsEnabled,
      // Receiving issues
      receivingIssuesUpdating,
      receivingIssuesError,
      showReceivingIssuesRow,
      receivingIssuesState,
      showReceivingIssueNotes,
      receivingIssueNotesDraft,
      saveReceivingIssueNotes,
      toggleReceivingIssueMissing,
      toggleReceivingIssueInaccurate,
      // Document preview
      documentError,
      isOpeningDocument,
      isLoadingPreview,
      previewObjectUrl,
      pdfIframeSrc,
      isPdfPreview,
      isImagePreview,
      // Extracted data display
      displayTrackingNumber,
      hasExtractedDetails,
      hasExtractedAdditionalDetails,
      hasParsedLineItems,
      hasAnyExtractedData,
      previewOnlyLayout,
      lineItems,
      formatDetailKey,
      formatDetailValue,
      openPackingListDocument,
      // Draft line items
      addLineItem,
      removeAddedItem,
      patchAdded
    }
  }
}
</script>
